<script setup>
import {ref, computed, watch, onMounted, getCurrentInstance} from 'vue'
import FormControlPlainText from "./form-control/FormControlPlainText.vue"

import imageCompression from 'browser-image-compression'

const props = defineProps({
    formId: String,
    fileId: Number,
    fileBrowser: Boolean,
    onChange: Function,
    tableName: String,
    columnName: String,
    readonly: Boolean,
    rowId: Number,
    removeOption: Boolean,
})

const {proxy} = getCurrentInstance()
const store = proxy.$store
const router = proxy.$router
const route = proxy.$route

// --- State ---

const fileInput = ref(null)
const dropzoneElement = ref(null)
const fileData = ref({})
const progress = ref(0)
const isDragging = ref(false)
const filesTableName = 'files'

// --- Computed ---

const thumbnailSrc = computed(() => {
    const obj = fileData.value
    if (obj?.fileName && isImage(obj?.mimetype)) {
        return store.getters.imageWidthSrc(obj.fileName, obj.width, obj.modifiedDate, 200)
    }
    return null
})

const inputAccept = computed(() => props.fileBrowser ? '' : 'image/*')

const showUploadOptions = computed(() => {
    return (
        (!fileData.value.id || props.tableName === filesTableName) &&
        !progress.value &&
        !props.readonly
    )
})

// --- Data Fetching ---

const fetchFileData = async (id) => {
    if (!id) {
        fileData.value = {}
        return
    }
    fileData.value = await store.dispatch('itemData/get', {tableName: 'files', id})
}

// Watch for prop changes (replaces asyncComputed)
watch(() => props.fileId, (newId) => fetchFileData(newId), {immediate: true})

// --- Methods ---

const chooseFile = () => fileInput.value?.click()

const applyChange = (fileObj) => {
    if (props.onChange) props.onChange(fileObj)
}

const removeFile = () => {
    if (fileInput.value) fileInput.value.value = ''
    progress.value = 0
    fileData.value = {}
    applyChange({id: 0})
}

const isImage = (mimetype) => mimetype?.startsWith('image/') && mimetype !== 'image/svg+xml'

const validateFileType = (type) => {
    if (props.fileBrowser) return true

    if (!type.startsWith('image/') || type === 'image/svg+xml') {
        const msg = type === 'image/svg+xml'
            ? 'SVGs cannot be uploaded to the Images component.'
            : 'The file must be an image.'
        store.dispatch('toasts/add', {body: msg})
        return false
    }
    return true
}

const uploadFile = async (file) => {
    // Sanitize filename for UI feedback
    let fileName = file.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z 0-9\-.]+/g, '')
        .toLowerCase()
        .replace(/ +/g, '-')

    const formData = new FormData()

    if (isImage(file.type) && file.type !== 'image/gif') {
        let targetMimeType = 'image/jpeg'

        // 1. Preserve PNGs that use transparency
        if (file.type === 'image/png' && await isTransparentPng(file)) {
            targetMimeType = 'image/png'
        }

        // 2. Update file extension
        if (targetMimeType === 'image/jpeg') {
            // Strip out the old extension (.png, .webp, .jpeg, etc.) and append .jpg
            // This regex looks for the last dot and everything after it
            fileName = fileName.replace(/\.[^/.]+$/, "") + ".jpg"
        }

        // 3. Display the name of the file in the UI
        fileData.value.fileName = fileName

        // Only apply 80% compression quality for larger images
        // Avoids small images appearing blurry.
        const quality = file.size < 30000  ? 1 : 0.8

        // 4. Compress the image natively in the browser
        let compressedBlob;
        try {
            compressedBlob = await imageCompression(file, {
                maxSizeMB: 10,             // Maximum file size fallback
                maxWidthOrHeight: 3000,    // Hard limit dimensions to 3000x3000px
                useWebWorker: true,        // Prevents UI stuttering during compression
                fileType: targetMimeType,  // Force output to JPEG
                initialQuality: quality
            });
        } catch (compressionError) {
            await store.dispatch('toasts/add', {
                body: '<strong>Failed to decode image.</strong> Please ensure it\'s a valid image format, try resaving the image, and upload again.',
                colourScheme: 'warning'
            });
            return
        }

        // 5. Convert Blob back to a File object for the upload payload
        const optimizedFile = new File([compressedBlob], fileName, {
            type: targetMimeType
        })

        formData.append('file', optimizedFile)
    } else {
        fileData.value.fileName = fileName

        formData.append('file', file)
    }

    if (props.rowId && props.tableName === filesTableName) {
        formData.append('existingFileId', props.rowId)
    }

    progress.value = 1

    store.dispatch('request/post', {
        url: `${store.state.apiEndpoint}/api/FilesController/upload`,
        postData: formData,
        onUploadProgress: (e) => {
            progress.value = Math.round((e.loaded / e.total) * 100)
        }
    }).then((response) => {
        const obj = response.data
        if (obj?.id) {
            fileData.value = obj

            if (props.tableName === filesTableName && parseInt(route.params.id) === 0) {
                router.push({params: {id: obj.id}})
            }
            applyChange(obj)
        }
        progress.value = 0
    }).catch((err) => {
        progress.value = 0
        if (err.response?.status === 409) {
            store.dispatch('toasts/add', {body: err.response.data.message})
            if (props.tableName !== filesTableName) fileData.value = {}
        }
    })
}

const onChangeFileInput = () => {
    const file = fileInput.value?.files[0]
    if (file && validateFileType(file.type)) {
        uploadFile(file)
    }
}

const openImageBrowser = () => {
    let hiddenFilters = {}
    if (!props.fileBrowser) {
        hiddenFilters = {
            mimetype: {startsWith: 'image/', ne: 'image/svg+xml'}
        }
    }

    store.dispatch('modals/show', {
        componentName: 'SelectListModal',
        obj: {
            listingName: `${filesTableName}.${props.columnName}`,
            selectedIds: props.fileId ? [props.fileId] : [],
            hiddenFilters,
            onSelect: async (selectedIds) => {
                const id = selectedIds.pop()
                const obj = await store.dispatch('itemData/get', {tableName: filesTableName, id})
                applyChange(obj)
                store.dispatch('modals/remove', obj.index)
            }
        }
    })
}

const showLightbox = () => {
    const src = store.getters.fileSrc(fileData.value.fileName)
    store.commit('lightbox/reset')
    store.commit('lightbox/add', {id: 0, title: '', src})
    store.dispatch('lightbox/show', 0)
}

// --- Drag & Drop ---

onMounted(() => {
    if (!props.onChange) return
    const el = dropzoneElement.value

    el.ondragenter = (e) => {
        e.preventDefault()
        isDragging.value = true
    }
    el.ondragleave = () => {
        isDragging.value = false
    }
    el.ondragover = (e) => {
        e.preventDefault()
        isDragging.value = true
    }
    el.ondrop = (e) => {
        e.preventDefault()
        isDragging.value = false
        const file = e.dataTransfer?.files[0]
        if (file && validateFileType(file.type)) uploadFile(file)
    }
})

/**
 * Helper function to detect true transparency in a PNG file
 */
async function isTransparentPng(file) {
    return new Promise((resolve) => {
        const img = new Image()
        img.src = URL.createObjectURL(file)

        img.onload = () => {
            // Clean up the memory URL object
            URL.revokeObjectURL(img.src)

            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')

            // Keep canvas small to make pixel checking lightning-fast
            canvas.width = Math.min(img.width, 500)
            canvas.height = Math.min(img.height, 500)

            // Draw the image to the canvas
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

            // Grab the raw RGBA pixel data
            const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height).data

            // Loop through the data array checking every 4th value (the Alpha channel)
            // imgData layout: [R, G, B, A,  R, G, B, A, ...]
            for (let i = 3; i < imgData.length; i += 4) {
                if (imgData[i] < 255) {
                    resolve(true) // Found a semi-transparent or transparent pixel
                    return
                }
            }
            resolve(false) // Entire image is completely opaque
        }

        img.onerror = () => {
            resolve(false) // Fallback to safe JPEG compression if reading fails
        }
    })
}
</script>

<template>
    <div ref="dropzoneElement" class="dropzone" :class="{ 'active': isDragging }">
        <div class="d-flex align-items-start">
            <div v-if="thumbnailSrc" class="me-3">
                <a href @click.prevent="showLightbox">
                    <img
                        :alt="fileData.fileName"
                        :class="{ 'form-field-image-img-progress': progress }"
                        :src="thumbnailSrc"
                    >
                </a>
            </div>

            <div class="media-body">
                <div>
                    <FormControlPlainText
                        v-if="fileData.fileName"
                        :value="fileData.fileName"
                        :form-id="formId"
                    />

                    <input
                        ref="fileInput"
                        :accept="inputAccept"
                        class="d-none"
                        type="file"
                        @change="onChangeFileInput"
                    >

                    <div v-if="showUploadOptions">
                        <span v-if="tableName === filesTableName">Drag file here or</span>
                        <span v-else>Drag file here,</span>

                        <span v-if="tableName !== filesTableName">
                            <button class="btn btn-light btn-sm" type="button" @click="openImageBrowser">
                                Browse
                            </button>
                            <span> or </span>
                        </span>

                        <button
                            class="btn btn-light btn-sm wizard-form-field-file-choose-file"
                            type="button"
                            @click="chooseFile"
                        >
                            Upload
                        </button>
                    </div>

                    <div v-if="progress" class="progress mt-2 mb-1" style="height: 5px;">
                        <div
                            :style="{ 'width': progress + '%' }"
                            class="progress-bar"
                            role="progressbar"
                        />
                    </div>

                    <div v-if="fileData.id && !readonly" class="d-flex align-items-center mt-3">
                        <button
                            v-if="tableName !== filesTableName"
                            class="btn btn-light btn-sm"
                            type="button"
                            @click="openImageBrowser"
                        >
                            Browse
                        </button>

                        <template v-if="removeOption">
                            <span class="ms-2">or</span>
                            <button class="btn btn-light btn-sm ms-2" type="button" @click="removeFile">
                                Remove
                            </button>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.dropzone {
    position: relative;
    margin: -10px 0 0 -10px;
    padding: 10px;
    border-radius: 5px;
    transition: background-color 0.2s;
}

.dropzone.active {
    background-color: #e3e3e3;
}
</style>